import { Padlock64 } from '@/assets/icons'
import { Button } from '@/components/Inputs'
import { PopupSkeleton } from '@/components/Popup'
import { gql } from '@/gql'
import { SlotState } from '@/gql/graphql.ts'
import { getErrorMessage } from '@/utils/errors.ts'
import { toastError, toastSuccess } from '@/utils/toast'
import { useMutation, useQuery } from '@apollo/client'
import { T } from '@tolgee/react'
import { useNavigate, useParams } from 'react-router-dom'
import invariant from 'tiny-invariant'

const SERVICE_DETAILS = gql(`
  query serviceAccountDetailsForReactivation($serviceId: String!) {
    service(where: { id: $serviceId }) {
      provider {
        price {
          offerReturn
        }
      }
      slots {
        id
        status
        usedBy {
          id
        }
      }
    }
  }
`)

const UNRESERVE_SLOT = gql(`
  mutation unreserveSlot($slotId: String!) {
    freeSlot(slotId: $slotId) {
      id
    }
  }
`)

export const SlotReactivate: React.FC = () => {
  const { id: serviceId } = useParams<{ id: string }>()
  invariant(serviceId)

  const navigate = useNavigate()

  const { data } = useQuery(SERVICE_DETAILS, {
    variables: { serviceId }
  })

  const [unreserveSlot] = useMutation(UNRESERVE_SLOT)

  const handleConfirm = async () => {
    invariant(data, 'handleConfirm can only be called once the data is loaded')

    try {
      const slotId = data.service?.slots.find(
        ({ status, usedBy }) => status === SlotState.Reserved && !usedBy
      )?.id

      invariant(slotId, 'Unable to find unused reserved slot')

      await unreserveSlot({ variables: { slotId } })
      toastSuccess('Slot was reactivated!')
      navigate(`/overview/subscriptions/offer/${serviceId}/overview`, {
        state: 'refetch'
      })
    } catch (e: unknown) {
      console.error(e)
      toastError(getErrorMessage(e))
    }
  }

  return (
    <PopupSkeleton showClose>
      <div className="w-[450px] h-full max-md:w-full p-10 text-center">
        <div className="flex flex-col items-center gap-2 mb-6">
          <div className="border border-dashed border-pink-brink rounded p-4">
            <Padlock64
              className="rounded-full shadow-[0_0_0_6px] shadow-pink-brink/20
                [&_path]:stroke-white [&_path]:fill-pink-brink"
            />
          </div>
          <h6 className="text-blue-dodger text-xs">
            <T
              keyName="components.offerSubscription.offerDetails.overview.reactivateUserPopup.monthlyPayment"
              params={{
                currency: () => '€',
                price: () =>
                  data?.service?.provider.price.offerReturn.toFixed(2) ?? '0.00'
              }}
            />
          </h6>
        </div>

        <h3 className="text-lg font-medium mb-4">
          <T keyName="components.offerSubscription.offerDetails.overview.reactivateUserPopup.title" />
        </h3>

        <h6 className="text-sm text-gray-shuttle-soft mb-4">
          <T
            keyName="components.offerSubscription.offerDetails.overview.reactivateUserPopup.subTitle"
            params={{
              price: () =>
                data?.service?.provider.price.offerReturn.toFixed(2) ?? '0.00',
              currency: () => '€'
            }}
          />
        </h6>

        <div className="flex justify-center gap-6 mt-8">
          <Button
            onClick={() => navigate(-1)}
            className="border-gray-shuttle text-gray-shuttle hover:bg-gray-shuttle hover:text-white"
          >
            <T keyName="components.offerSubscription.offerDetails.overview.reactivateUserPopup.cancel" />
          </Button>
          <Button onClick={handleConfirm}>
            <T keyName="components.offerSubscription.offerDetails.overview.reactivateUserPopup.reactivate" />
          </Button>
        </div>
      </div>
    </PopupSkeleton>
  )
}
